import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../Messages';
import { showApiError } from '../../utils/errorUtils';
import UOMSelect from '../Form/UOMSelect';
import { formatAppDate } from '../../utils/dateUtils';

const SupervisorApprovalDialog = ({ isOpen, onClose, task, onApprovalComplete }) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();
  const [loading, setLoading] = useState(false);
  const [approvedQuantity, setApprovedQuantity] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [uom, setUom] = useState('PCS');
  const [completionLogs, setCompletionLogs] = useState([]);

  useEffect(() => {
    if (isOpen && task?.task_id) {
      apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/completion-logs`))
        .then(res => res.ok ? res.json() : [])
        .then(logs => {
          setCompletionLogs(logs);
          if (logs.length > 0) {
            const total = logs.reduce((sum, log) => sum + (log.quantity_produced || 0), 0);
            setApprovedQuantity(String(total));
            if (logs[0].uom) setUom(logs[0].uom);
          }
        })
        .catch(() => setCompletionLogs([]));
    }
  }, [isOpen, task?.task_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!approvedQuantity || parseFloat(approvedQuantity) <= 0) {
      showError(t('taskApproval.errors.invalidQuantity'), t('taskApproval.errors.invalidQuantityDesc'));
      return;
    }

    setLoading(true);
    try {
      const approvalData = {
        quantity_produced: parseFloat(approvedQuantity),
        uom: uom,
        completion_notes: completionNotes || t('taskApproval.messages.defaultNotes', { quantity: approvedQuantity, uom })
      };

      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/approve-quantity`), {
        method: 'POST',
        body: JSON.stringify(approvalData)
      });

      if (response.ok) {
        showSuccess(
          t('taskApproval.messages.approved'),
          t('taskApproval.messages.approvedDesc', { code: task.task_code, quantity: approvedQuantity, uom })
        );
        onApprovalComplete();
        onClose();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error approving task quantity:', error);
      showError(t('taskApproval.errors.approvalFailed'), t('taskApproval.errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setApprovedQuantity('');
      setCompletionNotes('');
      setUom('PCS');
      setCompletionLogs([]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{t('taskApproval.title')}</h2>
          <button className="dialog-close" onClick={handleClose} disabled={loading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            <div className="task-info-section">
              <h3>{t('taskApproval.sections.taskInfo')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('taskApproval.fields.taskCode')}</label>
                  <span>{task.task_code}</span>
                </div>
                <div className="info-item">
                  <label>{t('taskApproval.fields.taskName')}</label>
                  <span>{task.task_name}</span>
                </div>
                <div className="info-item">
                  <label>{t('taskApproval.fields.completedBy')}</label>
                  <span>{task.username ? `${task.first_name || ''} ${task.last_name || ''} (${task.username})` : '—'}</span>
                </div>
                <div className="info-item">
                  <label>{t('taskApproval.fields.completedAt')}</label>
                  <span>{task.completed_at ? formatAppDate(task.completed_at, 'datetime') : '—'}</span>
                </div>
              </div>
            </div>

            {completionLogs.length > 0 && (
              <div className="shift-history-section">
                <h3>{t('taskApproval.sections.shiftHistory')}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.85em', color: '#6c757d' }}>{t('taskApproval.tableHeaders.operator')}</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.85em', color: '#6c757d' }}>{t('taskApproval.tableHeaders.quantity')}</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.85em', color: '#6c757d' }}>{t('taskApproval.tableHeaders.uom')}</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.85em', color: '#6c757d' }}>{t('taskApproval.tableHeaders.dateTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completionLogs.map((log, idx) => (
                      <tr key={log.log_id || idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '4px 8px', fontSize: '0.9em' }}>{log.operator_name?.trim() || log.username || t('taskApproval.labels.userFallback', { id: log.user_id })}</td>
                        <td style={{ padding: '4px 8px', fontSize: '0.9em', textAlign: 'right' }}>{log.quantity_produced}</td>
                        <td style={{ padding: '4px 8px', fontSize: '0.9em' }}>{log.uom}</td>
                        <td style={{ padding: '4px 8px', fontSize: '0.9em' }}>{log.completed_at ? formatAppDate(log.completed_at, 'datetime') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontWeight: 'bold', margin: '4px 0 0' }}>
                  {t('taskApproval.labels.totalFromShifts', {
                    total: completionLogs.reduce((s, l) => s + (l.quantity_produced || 0), 0).toFixed(3),
                    uom: completionLogs[0]?.uom || ''
                  })}
                </p>
              </div>
            )}

            <div className="approval-section">
              <h3>{t('taskApproval.sections.quantityApproval')}</h3>
              <p>{t('taskApproval.labels.instructions')}</p>

              <div className="form-group">
                <label>{t('taskApproval.fields.approvedQuantity')}</label>
                <input
                  type="number"
                  value={approvedQuantity}
                  onChange={(e) => setApprovedQuantity(e.target.value)}
                  placeholder={t('taskApproval.placeholders.quantity')}
                  min="0"
                  step="0.001"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>{t('taskApproval.fields.uom')}</label>
                <UOMSelect
                  value={uom}
                  onChange={(value) => setUom(value)}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>{t('taskApproval.fields.approvalNotes')}</label>
                <textarea
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder={t('taskApproval.placeholders.notes')}
                  rows="3"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="dialog-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {t('taskApproval.buttons.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !approvedQuantity}
            >
              {loading ? t('taskApproval.buttons.approving') : t('taskApproval.buttons.approve')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupervisorApprovalDialog;